import { invenioConfig } from "@config";
import DocumentAuthors from "@modules/Document/DocumentAuthors";
import DocumentLanguages from "@modules/Document/DocumentLanguages";
import LiteratureCover from "@modules/Literature/LiteratureCover";
import LiteratureEdition from "@modules/Literature/LiteratureEdition";
import LiteratureTags from "@modules/Literature/LiteratureTags";
import LiteratureTitle from "@modules/Literature/LiteratureTitle";
import MARC21Preview from "@components/MARC21Preview";
import { BackOfficeRoutes } from "@routes/urls";
import _get from "lodash/get";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Link } from "react-router-dom";
import {
  Grid,
  Header,
  Item,
  List,
  Label,
  Icon,
  Button,
} from "semantic-ui-react";
import DocumentCirculation from "./DocumentCirculation";
import { renderSubtitle } from "@modules/Document/utils";
import UnflagButton from "@components/backoffice/buttons/UnflaggedButton/UnflagButton";

export default class DocumentListEntry extends Component {
  state = {
    isLocallyUnflagged: false,
  };

  handleUnflagSuccess = (data, documentPid) => {
    this.setState({ isLocallyUnflagged: true });
  };

  renderMiddleColumn = (document) => {
    const { renderMiddleColumn } = this.props;
    if (renderMiddleColumn) {
      return renderMiddleColumn(document);
    }
    return <DocumentCirculation document={document} />;
  };

  renderRightColumn = (document) => {
    const { renderRightColumn } = this.props;
    if (renderRightColumn) {
      return renderRightColumn(document);
    }
    return this.renderRelations();
  };

  renderWeedingStatus = () => {
    const { record: document } = this.props;
    const { isLocallyUnflagged } = this.state;

    if (!document.metadata?.weeding?.is_flagged || isLocallyUnflagged) {
      return null;
    }

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5em",
          marginBottom: "0.5em",
        }}
      >
        <Label basic color="red" size="small">
          <Icon name="flag" />
          Flagged for deletion
        </Label>
        <UnflagButton
          documentPid={document.metadata.pid}
          size="mini"
          compact
          showText={false}
          onSuccess={this.handleUnflagSuccess}
        />
      </div>
    );
  };

  renderWeededOutStatus = () => {
    const { record: document } = this.props;

    if (document.metadata?.weed_out === true) {
      return (
        <Label color="red" size="small" style={{ marginBottom: "0.5em" }}>
          <Icon name="ban" />
          REMOVED
        </Label>
      );
    }

    return null;
  };

  renderRelations = () => {
    const { record: document } = this.props;
    const partOfMMQuery = _get(
      document,
      "metadata.relations.multipart_monograph",
      []
    )
      .map((rel) => `pid:${rel.pid_value}`)
      .join(" OR ");
    const partOfSeriesQuery = _get(document, "metadata.relations.serial", [])
      .map((rel) => `pid:${rel.pid_value}`)
      .join(" OR ");

    return (
      <Item.Description>
        <List verticalAlign="middle" className="document-relations">
          {partOfMMQuery && (
            <List.Item>
              <List.Content floated="right">
                <Link
                  to={BackOfficeRoutes.seriesListWithQuery(partOfMMQuery)}
                />
              </List.Content>
            </List.Item>
          )}
          {partOfSeriesQuery && (
            <List.Item>
              <List.Content floated="right">
                <Link
                  to={BackOfficeRoutes.seriesListWithQuery(partOfSeriesQuery)}
                />
              </List.Content>
            </List.Item>
          )}
          {document.metadata.eitems.total > 0 ? <List.Item /> : null}
        </List>
      </Item.Description>
    );
  };

  // MARC21 Binary Export Method
  exportMARC21 = async (document) => {
    try {
      const documentId = document.metadata.pid;
      // const timestamp = new Date().getTime();
      const apiUrl = `/api/documents/${documentId}?format=marc21&_t=${new Date().getTime()}`;
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Accept: "application/marc",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentId}_marc21.mrc`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(`Failed to export MARC21: ${error.message}`);
    }
  };

  // MARCXML Export Method
  exportMARCXML = async (document) => {
    try {
      const documentId = document.metadata.pid;
      // const timestamp = new Date().getTime();
      const apiUrl = `/api/documents/${documentId}?format=marcxml&_t=${new Date().getTime()}`;
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Accept: "application/marcxml+xml",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const xmlData = await response.text();

      // Create download link
      const blob = new Blob([xmlData], { type: "application/xml" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentId}_marc21.xml`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(`Failed to export MARCXML: ${error.message}`);
    }
  };

  renderMARC21Actions = (document) => {
    return (
      <div style={{ marginTop: "0.5em" }}>
        <MARC21Preview
          documentId={document.metadata.pid}
          isEditMode={false}
          trigger={
            <Button
              size="mini"
              basic
              icon="file code outline"
              content="MARC21 Preview"
              title="Preview MARC21 record"
            />
          }
        />
      </div>
    );
  };

  render() {
    const { record: document } = this.props;
    const isWeededOut = document.metadata?.weed_out === true;

    const itemStyle = isWeededOut
      ? {
          backgroundColor: "#f8f9fa",
          position: "relative",
        }
      : {};

    return (
      <Item style={itemStyle}>
        <div className="item-image-wrapper">
          <LiteratureCover
            asItem
            isRestricted={_get(document, "metadata.restricted", false)}
            linkTo={BackOfficeRoutes.documentDetailsFor(document.metadata.pid)}
            size="tiny"
            url={_get(document, "metadata.cover_metadata.urls.medium")}
          />
          <Header disabled as="h6" className="document-type tiny ellipsis">
            {document.metadata.document_type}
          </Header>
        </div>
        <Item.Content>
          <Item.Header
            as={Link}
            to={BackOfficeRoutes.documentDetailsFor(document.metadata.pid)}
            data-test={`navigate-${document.metadata.pid}`}
          >
            <LiteratureTitle
              title={document.metadata.title}
              truncateWidth="500px"
            />
          </Item.Header>
          <Grid columns={3}>
            <Grid.Column computer={6} largeScreen={5}>
              <Item.Meta className="document-authors">
                {renderSubtitle(document.metadata?.alternative_titles)}
                <DocumentAuthors
                  authors={document.metadata.authors}
                  hasOtherAuthors={document.metadata.other_authors}
                  prefix="by "
                  limit={invenioConfig.LITERATURE.authors.maxDisplay}
                />
              </Item.Meta>

              <div style={{ marginBottom: "0.5em" }}>
                {this.renderWeedingStatus()}
                {this.renderWeededOutStatus()}
              </div>

              <DocumentLanguages
                languages={document.metadata.languages}
                prefix={<label>languages </label>}
              />
              <Item.Description>
                {document.metadata.edition && (
                  <LiteratureEdition
                    edition={document.metadata.edition}
                    withLabel
                  />
                )}
              </Item.Description>
              <Item.Description>
                <label>Publication year</label>{" "}
                {document.metadata.publication_year}
              </Item.Description>
              <Item.Description>
                {document.metadata.imprint?.publisher && (
                  <>
                    <label> Publisher </label>
                    {document.metadata.imprint.publisher}
                  </>
                )}
              </Item.Description>

              {/* MARC21 actions including the export button */}
              {this.renderMARC21Actions(document)}
            </Grid.Column>
            <Grid.Column computer={3} largeScreen={4}>
              {this.renderMiddleColumn(document)}
            </Grid.Column>
            <Grid.Column computer={3} largeScreen={4}>
              {this.renderRightColumn(document)}
            </Grid.Column>
          </Grid>
          <Item.Extra>
            <LiteratureTags tags={document.metadata.tags} />
          </Item.Extra>
        </Item.Content>
        <div className="pid-field discrete">#{document.metadata.pid}</div>
      </Item>
    );
  }
}

DocumentListEntry.propTypes = {
  record: PropTypes.object.isRequired,
  renderMiddleColumn: PropTypes.func,
  renderRightColumn: PropTypes.func,
};

DocumentListEntry.defaultProps = {
  renderMiddleColumn: null,
  renderRightColumn: null,
};
